import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import {
  FaChevronDown,
  FaChevronRight,
  FaSearch,
  FaCheck,
  FaCheckCircle,
  FaMinus,
  FaCog,
  FaSave,
  FaUndo,
  FaListUl,
  FaAngleDoubleDown,
  FaAngleDoubleUp,
  FaExclamationTriangle,
  FaPlus,
  FaTimes,
  FaTimesCircle,
  FaSpinner,
  FaPen,
  FaLock,
  FaHome,
} from 'react-icons/fa';
import PageHeader from '../../../components/layout/PageHeader';
import Button from '../../../components/common/Button';
import Badge from '../../../components/common/Badge';
import { translate, getClientDomain, getToken } from '../../../utils/Common';
import { verifyAccessViewPermissions } from '../../../config/permissions';
import AuthContext from '../../../contexts/auth';
import styles from './Configuration.module.css';
import {
  moduleTriState,
  moduleSubtreeProgress,
  modChildren,
  collectDescendantModuleIds,
  computeDiff,
  countDiffForRole,
  deepClonePerms,
  getActionsForModule,
  withDepsEnabled,
  afterRevokeView,
  BLI_ROLE_MODULE_ONLY_CODE,
  isActionEnabledInSlice,
  migrateActionCodeInPerms,
} from './permissionsHelpers';
import { usePermissionsCatalog } from './usePermissionsCatalog';

/** Valores típicos de `Value` en ConfObjetosDetallesDescripciones (IdObjeto = 71). */
function ModalStatusHeader({ variant, title }) {
  const iconSize = 26;
  const icon =
    variant === 'success' ? (
      <FaCheckCircle size={iconSize} aria-hidden />
    ) : variant === 'warning' ? (
      <FaExclamationTriangle size={iconSize} aria-hidden />
    ) : (
      <FaTimesCircle size={iconSize} aria-hidden />
    );
  const iconClass =
    variant === 'success'
      ? styles.modalStatusSuccess
      : variant === 'warning'
        ? styles.modalStatusWarning
        : styles.modalStatusError;
  return (
    <div className={styles.modalHeaderRow}>
      <span className={`${styles.modalStatusIcon} ${iconClass}`}>{icon}</span>
      <div className={styles.modalTitle}>{title}</div>
    </div>
  );
}

function TriCheckbox({ state, onClick, ariaLabel, disabled = false }) {
  const cls = [styles.cb];
  if (state === 'checked') cls.push(styles.cbOn);
  if (state === 'indeterminate') cls.push(styles.cbMid);
  if (disabled) cls.push(styles.cbDisabled);
  // role="checkbox" rather than a plain button: a button does not support
  // aria-checked, and this control really is a tri-state checkbox
  // ("mixed" = indeterminate).
  return (
    <button
      type="button"
      className={cls.join(' ')}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onClick();
      }}
      aria-label={ariaLabel}
      role="checkbox"
      aria-checked={state === 'indeterminate' ? 'mixed' : state === 'checked'}
    >
      {state === 'checked' && <FaCheck size={10} />}
      {state === 'indeterminate' && <FaMinus size={10} color="var(--brand-pink, #e9257d)" />}
    </button>
  );
}

function Switch({ on, disabled, onToggle, ariaLabel }) {
  return (
    <button
      type="button"
      className={`${styles.switch} ${on ? styles.switchOn : ''}`}
      onClick={onToggle}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={on}
    >
      <span className={styles.switchKnob} />
    </button>
  );
}

export default function PermissionsEditor() {
  const history = useHistory();
  const location = useLocation();
  const auth = React.useContext(AuthContext);
  const permissionsPath = '/dashboard/settings';
  const permissionsPathAlt = '/dashboard/configuracion';
  const canUpdate =
    verifyAccessViewPermissions(['update'], { path: permissionsPath }) ||
    verifyAccessViewPermissions(['update'], { path: permissionsPathAlt });
  const canCreate =
    verifyAccessViewPermissions(['create'], { path: permissionsPath }) ||
    verifyAccessViewPermissions(['create'], { path: permissionsPathAlt });
  const catalog = usePermissionsCatalog();
  const {
    load,
    loading,
    error,
    permissionDenied,
    source,
    modules,
    createModuleLocal,
    createModuleApi,
    createActionLocal,
    createActionApi,
    updateActionCodeLocal,
    updateActionCodeApi,
    updateModuleLocal,
    updateModuleApi,
    customByModule,
    granularCatalog,
    roles,
    initialPerms,
    mockStandardActions,
  } = catalog;

  const standardActions = useMemo(
    () => {
      if (granularCatalog) return [];
      return mockStandardActions.map((a) => ({
        ...a,
        label: translate(a.labelKey),
        desc: translate(a.descKey),
        deps: a.deps || [],
      }));
    },
    [granularCatalog, mockStandardActions]
  );

  const resolveCustom = useCallback(
    (mid) => {
      const list = customByModule[mid] || [];
      return list.map((a) => ({
        ...a,
        label: a.labelKey ? translate(a.labelKey) : a.label || a.code,
        desc: a.descKey ? translate(a.descKey) : a.desc || '',
        deps: a.deps || [],
      }));
    },
    [customByModule]
  );

  const [perms, setPerms] = useState({});
  const [saved, setSaved] = useState({});
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [roleSearch, setRoleSearch] = useState('');
  const [modSearch, setModSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [noAccessAcknowledged, setNoAccessAcknowledged] = useState(false);
  const [showLogoutAfterNoAccess, setShowLogoutAfterNoAccess] = useState(false);
  const [pendingRoleId, setPendingRoleId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [roleAssignmentsLoading, setRoleAssignmentsLoading] = useState(false);
  const roleFetchSeqRef = useRef(0);
  const urlSyncInFlightRef = useRef(false);
  const mountedRef = useRef(false);
  const [createModuleDraft, setCreateModuleDraft] = useState({
    moduleName: '',
    path: '',
    parentModuleId: '',
  });
  const [createModuleError, setCreateModuleError] = useState(null);
  const [createModuleSubmitting, setCreateModuleSubmitting] = useState(false);
  const [editModuleDraft, setEditModuleDraft] = useState({
    moduleName: '',
    path: '',
    parentModuleId: '',
  });
  const [editModuleError, setEditModuleError] = useState(null);
  const [editModuleSubmitting, setEditModuleSubmitting] = useState(false);
  const [createActionDraft, setCreateActionDraft] = useState({
    actionCode: '',
    active: true,
  });
  const [createActionError, setCreateActionError] = useState(null);
  const [createActionSubmitting, setCreateActionSubmitting] = useState(false);
  /** Bloquea otros “Crear…” y dobles envíos mientras hay POST (API) o submit local en curso. */
  const creatingModule = createModuleSubmitting;
  const creatingAction = createActionSubmitting;
  /** Evita enviar `actionCode` obsoleto si el estado no coincidió con el input (p. ej. al pulsar Create). */
  const createActionCodeInputRef = useRef(null);
  const [editActionDraft, setEditActionDraft] = useState(null);
  const [editActionError, setEditActionError] = useState(null);
  const [editActionSubmitting, setEditActionSubmitting] = useState(false);
  const [saveErrorDetail, setSaveErrorDetail] = useState(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      roleFetchSeqRef.current += 1;
    };
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (loading) return;
    if (noAccessAcknowledged) return;
    if (permissionDenied || source === 'error' || error) {
      setModal('noCatalogAccess');
    }
  }, [loading, permissionDenied, source, error, noAccessAcknowledged]);

  useEffect(() => {
    if (loading) return;
    const base = {};
    roles.forEach((r) => {
      base[String(r.id)] = {};
      modules.forEach((m) => {
        base[String(r.id)][String(m.id)] = {};
      });
    });
    if (initialPerms && Object.keys(initialPerms).length > 0) {
      const merged = deepClonePerms(initialPerms);
      setPerms(merged);
      setSaved(deepClonePerms(merged));
    } else {
      setPerms(base);
      setSaved(deepClonePerms(base));
    }
  }, [loading, roles, modules, initialPerms]);

  /**
   * Hidratar rol/módulo desde la URL antes del efecto que hace history.replace
   * (evita que Atrás/Adelante quede pisado en el mismo ciclo).
   */
  useLayoutEffect(() => {
    if (loading || roles.length === 0) return;
    if (urlSyncInFlightRef.current) {
      urlSyncInFlightRef.current = false;
      return;
    }

    const params = new URLSearchParams(location.search);
    const rParam = params.get('roleId');
    let nextRoleId = roles[0].id;
    if (rParam != null && rParam !== '') {
      const match = roles.find((r) => String(r.id) === String(rParam));
      if (match) nextRoleId = match.id;
    }
    setSelectedRoleId((prev) => (String(prev) === String(nextRoleId) ? prev : nextRoleId));

    if (params.has('moduleId')) {
      const mParam = params.get('moduleId');
      let nextModuleId = null;
      if (mParam != null && mParam !== '' && modules.length > 0) {
        const mm = modules.find((m) => String(m.id) === String(mParam));
        if (mm) nextModuleId = String(mm.id);
      }
      setSelectedModuleId((prev) =>
        String(prev || '') === String(nextModuleId || '') ? prev : nextModuleId
      );
    } else {
      setSelectedModuleId((prev) => (prev == null || prev === '' ? prev : null));
    }
  }, [loading, location.search, roles, modules]);

  /** Mantener ?roleId= y ?moduleId= al cambiar selección (otros query params se conservan). */
  useEffect(() => {
    if (loading || roles.length === 0 || selectedRoleId == null) return;
    const params = new URLSearchParams(location.search);
    const curRole = params.get('roleId');
    const curMod = params.get('moduleId');
    const nextMod =
      selectedModuleId != null && selectedModuleId !== '' ? String(selectedModuleId) : '';
    if (String(curRole || '') === String(selectedRoleId) && String(curMod || '') === nextMod) {
      return;
    }
    params.set('roleId', String(selectedRoleId));
    if (nextMod) params.set('moduleId', nextMod);
    else params.delete('moduleId');
    const qs = params.toString();
    const nextSearch = qs ? `?${qs}` : '';
    if (location.search === nextSearch) return;

    urlSyncInFlightRef.current = true;
    history.replace({
      pathname: location.pathname,
      search: nextSearch,
      hash: location.hash || undefined,
    });
  }, [selectedRoleId, selectedModuleId, loading, roles.length, history, location.pathname, location.search, location.hash]);

  /** Si el módulo seleccionado es hijo, expandir padres (útil al abrir con ?moduleId=). */
  useEffect(() => {
    if (!selectedModuleId || modules.length === 0) return;
    const ancestorIds = [];
    let cur = modules.find((m) => String(m.id) === String(selectedModuleId));
    const guard = new Set();
    while (cur && cur.parentModuleId != null && String(cur.parentModuleId).trim() !== '') {
      const pid = String(cur.parentModuleId);
      if (guard.has(pid)) break;
      guard.add(pid);
      ancestorIds.push(pid);
      cur = modules.find((m) => String(m.id) === pid);
    }
    if (ancestorIds.length === 0) return;
    setExpanded((ex) => {
      const next = { ...ex };
      ancestorIds.forEach((id) => {
        next[id] = true;
      });
      return next;
    });
  }, [selectedModuleId, modules]);

  const moduleLabel = (m) =>
    m.moduleNameKey ? translate(m.moduleNameKey) : m.moduleName || m.id;

  const roleLabel = (r) => (r.nameKey ? translate(r.nameKey) : r.name || String(r.id));

  const fetchAssignmentsForRole = useCallback(
    async (roleId) => {
      if (source !== 'api') return;
      const seq = ++roleFetchSeqRef.current;
      setRoleAssignmentsLoading(true);
      const token = getToken();
      const base = getClientDomain(2);
      try {
        const res = await fetch(
          `${base}/users/bli-permissions/settings?roleId=${encodeURIComponent(roleId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (seq !== roleFetchSeqRef.current || !mountedRef.current) return;
        if (data.code !== 200 || !Array.isArray(data.payload?.assignments)) return;

        setPerms((prev) => {
          const next = deepClonePerms(prev);
          const rid = String(roleId);
          if (!next[rid]) next[rid] = {};
          modules.forEach((m) => {
            const mid = String(m.id);
            if (!next[rid][mid]) next[rid][mid] = {};
            Object.keys(next[rid][mid]).forEach((k) => delete next[rid][mid][k]);
          });
          data.payload.assignments.forEach((row) => {
            const mid = String(row.moduleId ?? row.ModuleId);
            const code = row.actionCode ?? row.ActionCode;
            if (!mid || code == null || String(code).trim() === '') return;
            if (!next[rid][mid]) next[rid][mid] = {};
            next[rid][mid][String(code)] = true;
          });
          queueMicrotask(() => {
            if (seq === roleFetchSeqRef.current && mountedRef.current) {
              setSaved(deepClonePerms(next));
            }
          });
          return next;
        });
      } catch (e) {
        console.warn('fetchAssignmentsForRole', e);
      } finally {
        if (seq === roleFetchSeqRef.current && mountedRef.current) setRoleAssignmentsLoading(false);
      }
    },
    [source, modules]
  );

  useEffect(() => {
    if (source === 'api' && selectedRoleId != null && !loading) {
      fetchAssignmentsForRole(selectedRoleId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoleId, source, loading]);

  useEffect(() => {
    if (source !== 'api') setRoleAssignmentsLoading(false);
  }, [source]);

  const diff = useMemo(
    () =>
      computeDiff(perms, saved, roles, modules, standardActions, customByModule, selectedRoleId),
    [perms, saved, roles, modules, standardActions, customByModule, selectedRoleId]
  );

  const dirty = diff.added + diff.removed > 0;
  const showRoleSwitchOverlay = source === 'api' && roleAssignmentsLoading;

  const renderRoleSwitchOverlay = () =>
    showRoleSwitchOverlay ? (
      <div
        className={styles.roleSwitchOverlay}
        aria-busy="true"
        aria-live="polite"
        aria-label={translate('dashboard.configuration.loadingRoleAssignments')}
      >
        <FaSpinner className={styles.roleSwitchSpinner} size={28} aria-hidden />
        <span className={styles.roleSwitchLabel}>
          {translate('dashboard.configuration.loadingRoleAssignments')}
        </span>
      </div>
    ) : null;

  const trySelectRole = (id) => {
    if (dirty && selectedRoleId != null && String(selectedRoleId) !== String(id)) {
      setPendingRoleId(id);
      setModal('unsavedRole');
      return;
    }
    setSelectedRoleId(id);
    setSelectedModuleId(null);
  };

  const discardAndSelectPending = () => {
    if (selectedRoleId != null) {
      setPerms((p) => {
        const next = deepClonePerms(p);
        next[String(selectedRoleId)] = deepClonePerms(saved)[String(selectedRoleId)] || {};
        return next;
      });
    }
    if (pendingRoleId != null) setSelectedRoleId(pendingRoleId);
    setPendingRoleId(null);
    setModal(null);
  };

  const saveAndSelectPending = async () => {
    await handleSave(false);
    if (pendingRoleId != null) setSelectedRoleId(pendingRoleId);
    setPendingRoleId(null);
    setModal(null);
  };

  const toggleModuleAll = (moduleId) => {
    if (!canUpdate) return;
    if (selectedRoleId == null) return;
    const rid = String(selectedRoleId);
    const mids = collectDescendantModuleIds(moduleId, modules);
    const state = moduleTriState(rid, moduleId, perms, modules, standardActions, customByModule);
    const turnOn = state !== 'checked';

    setPerms((prev) => {
      const next = deepClonePerms(prev);
      if (!next[rid]) next[rid] = {};
      mids.forEach((mid) => {
        if (!next[rid][mid]) next[rid][mid] = {};
        const customActs = resolveCustom(mid);
        const acts = getActionsForModule(mid, standardActions, customByModule);
        if (acts.length > 0) {
          if (turnOn) {
            acts.forEach((a) => {
              next[rid][mid] = withDepsEnabled(next[rid][mid], a, true);
            });
          } else {
            const cleared = { [BLI_ROLE_MODULE_ONLY_CODE]: false };
            acts.forEach((a) => {
              cleared[a.code] = false;
            });
            next[rid][mid] = afterRevokeView(cleared, standardActions, customActs);
          }
          next[rid][mid][BLI_ROLE_MODULE_ONLY_CODE] = false;
        } else {
          next[rid][mid] = { [BLI_ROLE_MODULE_ONLY_CODE]: !!turnOn };
        }
      });
      return next;
    });
  };

  const toggleAction = (moduleId, action) => {
    if (!canUpdate) return;
    if (selectedRoleId == null) return;
    const rid = String(selectedRoleId);
    const mid = String(moduleId);
    setPerms((prev) => {
      const next = deepClonePerms(prev);
      if (!next[rid]) next[rid] = {};
      if (!next[rid][mid]) next[rid][mid] = {};
      const slot = { ...next[rid][mid] };
      const actsHere = getActionsForModule(mid, standardActions, customByModule);
      const cur = isActionEnabledInSlice(slot, action.code, actsHere.length);
      if (!cur) {
        next[rid][mid] = withDepsEnabled(
          { ...slot, [BLI_ROLE_MODULE_ONLY_CODE]: false },
          action,
          true
        );
      } else if (action.code === 'view') {
        next[rid][mid] = afterRevokeView(
          { ...slot, view: false, [BLI_ROLE_MODULE_ONLY_CODE]: false },
          standardActions,
          resolveCustom(mid)
        );
      } else {
        slot[action.code] = false;
        next[rid][mid] = slot;
      }
      const finalSlice = next[rid][mid];
      if (actsHere.length > 0) {
        const anyOn = actsHere.some((a) => !!finalSlice[a.code]);
        // Si aún hay asociación RS4 (módulo en rol sin filas de acción), mantenerla al apagar todas las acciones.
        finalSlice[BLI_ROLE_MODULE_ONLY_CODE] = !anyOn;
      }
      return next;
    });
  };

  const expandAll = () => {
    const next = {};
    modules.forEach((m) => {
      next[String(m.id)] = true;
    });
    setExpanded(next);
  };

  const collapseAll = () => {
    const next = {};
    modules.forEach((m) => {
      next[String(m.id)] = false;
    });
    setExpanded(next);
  };

  const handleSave = async (showModal = true) => {
    if (!canUpdate) return;
    if (selectedRoleId == null) return;
    const rid = String(selectedRoleId);
    if (source === 'api') {
      setSaving(true);
      try {
        const token = getToken();
        const base = getClientDomain(2);
        const assignments = [];
        modules.forEach((m) => {
          const mid = String(m.id);
          const acts = getActionsForModule(mid, standardActions, customByModule);
          if (acts.length === 0) {
            if (perms[rid]?.[mid]?.[BLI_ROLE_MODULE_ONLY_CODE]) {
              assignments.push({
                moduleId: Number(mid) || mid,
                actionCode: BLI_ROLE_MODULE_ONLY_CODE,
                actionId: null,
              });
            }
            return;
          }
          acts.forEach((a) => {
            if (a.code === BLI_ROLE_MODULE_ONLY_CODE) return;
            if (perms[rid]?.[mid]?.[a.code]) {
              const row = (customByModule[mid] || []).find((x) => x.code === a.code);
              assignments.push({
                moduleId: Number(mid) || mid,
                actionCode: a.code,
                actionId: row?.actionId ?? a.actionId ?? null,
              });
            }
          });
          if (perms[rid]?.[mid]?.[BLI_ROLE_MODULE_ONLY_CODE]) {
            assignments.push({
              moduleId: Number(mid) || mid,
              actionCode: BLI_ROLE_MODULE_ONLY_CODE,
              actionId: null,
            });
          }
        });
        const res = await fetch(`${base}/users/bli-permissions/roles/${rid}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ assignments }),
        });
        const data = await res.json().catch(() => ({}));
        if (data.code !== 200) {
          setSaveErrorDetail(data.detail || data.message || 'SAVE_FAILED');
          setModal('saveError');
          return;
        }
        setSaved(deepClonePerms(perms));
        setSaveErrorDetail(null);
        await fetchAssignmentsForRole(selectedRoleId);
        const currentUserRoleId = auth?.user?.roleId;
        if (
          currentUserRoleId != null &&
          String(currentUserRoleId) === String(selectedRoleId) &&
          typeof auth?.refreshBliPermissions === 'function'
        ) {
          const refreshed = await auth.refreshBliPermissions();
          if (!refreshed?.ok) {
            console.warn('PermissionsEditor: refreshBliPermissions after own-role save failed', refreshed?.reason);
          }
        }
        if (showModal) setModal('saved');
      } catch (e) {
        console.error(e);
        setSaveErrorDetail(e.message || 'SAVE_FAILED');
        setModal('saveError');
      } finally {
        setSaving(false);
      }
    } else {
      setSaved(deepClonePerms(perms));
      if (showModal) setModal('saved');
    }
  };

  const handleDiscard = () => {
    if (!canUpdate) return;
    if (selectedRoleId == null) return;
    const rid = String(selectedRoleId);
    setPerms((p) => {
      const next = deepClonePerms(p);
      next[rid] = deepClonePerms(saved)[rid] || {};
      return next;
    });
  };

  const openCreateModule = () => {
    if (!canCreate || creatingModule || creatingAction) return;
    setCreateModuleDraft({ moduleName: '', path: '', parentModuleId: '' });
    setCreateModuleError(null);
    setModal('createModule');
  };

  const openEditModule = () => {
    if (!canUpdate) return;
    const m = modules.find((x) => String(x.id) === String(selectedModuleId));
    if (!m) return;
    setEditModuleDraft({
      moduleName: m.moduleName || '',
      path: m.path || '',
      parentModuleId:
        m.parentModuleId == null || m.parentModuleId === '' ? '' : String(m.parentModuleId),
    });
    setEditModuleError(null);
    setModal('editModule');
  };

  const openCreateAction = () => {
    if (!canCreate || creatingModule || creatingAction) return;
    if (!selectedModuleId) return;
    setCreateActionDraft({
      actionCode: '',
      active: true,
    });
    setCreateActionError(null);
    setModal('createAction');
  };

  const openEditActionCode = (action) => {
    if (!canUpdate) return;
    if (!selectedModuleId || !action?.actionId) return;
    setEditActionError(null);
    const code = String(action.code || '');
    setEditActionDraft({
      actionId: action.actionId,
      code,
      originalCode: code,
      displayLabel: String(action.label || action.code || ''),
    });
    setModal('editAction');
  };

  const submitEditActionCode = async () => {
    if (!canUpdate) return;
    setEditActionError(null);
    const mid = selectedModuleId;
    const draft = editActionDraft;
    if (mid == null || draft == null || draft.actionId == null) return;
    const next = String(draft.code || '').trim();
    const prev = String(draft.originalCode || '');
    if (!next) {
      setEditActionError(translate('dashboard.configuration.actionCodeRequired'));
      return;
    }
    if (next === prev) {
      setModal(null);
      setEditActionDraft(null);
      return;
    }
    setEditActionSubmitting(true);
    try {
      let res;
      if (source === 'api') {
        res = await updateActionCodeApi(mid, draft.actionId, next);
      } else {
        res = updateActionCodeLocal(mid, draft.actionId, next);
      }
      if (!res?.ok) {
        const raw = res?.detail || res?.error || 'UPDATE_FAILED';
        let message = String(raw);
        if (message.includes('DUPLICATE_ACTION_CODE')) {
          message = translate('dashboard.configuration.duplicateActionCode');
        } else if (
          message === 'BLI_MODULE_ACTION_UPDATE_VALIDATION' ||
          message === 'UPDATE_FAILED' ||
          message === 'BLI_MODULE_ACTION_CREATE_VALIDATION'
        ) {
          message = translate('dashboard.configuration.renameActionCodeFailed');
        } else if (message === 'ACTION_CODE_REQUIRED') {
          message = translate('dashboard.configuration.actionCodeRequired');
        }
        setEditActionError(message);
        return;
      }
      setPerms((p) => migrateActionCodeInPerms(p, String(mid), prev, next));
      setSaved((p) => migrateActionCodeInPerms(p, String(mid), prev, next));
      setModal(null);
      setEditActionDraft(null);
    } finally {
      setEditActionSubmitting(false);
    }
  };

  const submitCreateAction = async () => {
    if (!canCreate) return;
    setCreateActionError(null);
    const mid = selectedModuleId;
    if (mid == null) return;
    const codeFromInput = String(createActionCodeInputRef.current?.value ?? createActionDraft.actionCode ?? '').trim();
    const draftForSubmit = { ...createActionDraft, actionCode: codeFromInput };
    if (source === 'api') {
      setCreateActionSubmitting(true);
      try {
        const res = await createActionApi?.(mid, draftForSubmit);
        if (!res?.ok) {
          setCreateActionError(res?.detail || res?.error || 'CREATE_FAILED');
          return;
        }
        setModal(null);
      } finally {
        setCreateActionSubmitting(false);
      }
      return;
    }
    setCreateActionSubmitting(true);
    try {
      const res = createActionLocal?.(mid, draftForSubmit);
      if (!res?.ok) {
        setCreateActionError(res?.error || 'CREATE_FAILED');
        return;
      }
      setModal(null);
    } finally {
      setCreateActionSubmitting(false);
    }
  };

  const submitEditModule = async () => {
    if (!canUpdate) return;
    setEditModuleError(null);
    const mid = selectedModuleId;
    if (mid == null) return;
    if (source === 'api') {
      setEditModuleSubmitting(true);
      try {
        const res = await updateModuleApi?.(mid, editModuleDraft);
        if (!res?.ok) {
          setEditModuleError(res?.detail || res?.error || 'UPDATE_FAILED');
          return;
        }
        setModal(null);
      } finally {
        setEditModuleSubmitting(false);
      }
      return;
    }
    const res = updateModuleLocal?.(mid, editModuleDraft);
    if (!res?.ok) {
      setEditModuleError(res?.error || 'UPDATE_FAILED');
      return;
    }
    setModal(null);
  };

  /** Módulos que no pueden ser padre: el propio y todos sus descendientes (evita ciclos). */
  const parentExcludeIdsForEdit = useMemo(() => {
    if (selectedModuleId == null) return new Set();
    return new Set(collectDescendantModuleIds(selectedModuleId, modules).map(String));
  }, [selectedModuleId, modules]);

  const submitCreateModule = async () => {
    if (!canCreate) return;
    setCreateModuleError(null);
    if (source === 'api') {
      setCreateModuleSubmitting(true);
      try {
        const res = await createModuleApi?.({
          moduleName: createModuleDraft.moduleName,
          path: createModuleDraft.path,
          parentModuleId: createModuleDraft.parentModuleId,
        });
        if (!res?.ok) {
          setCreateModuleError(res?.detail || res?.error || 'CREATE_FAILED');
          return;
        }
        setModal(null);
      } finally {
        setCreateModuleSubmitting(false);
      }
      return;
    }
    setCreateModuleSubmitting(true);
    try {
      const res = createModuleLocal?.({
        moduleName: createModuleDraft.moduleName,
        path: createModuleDraft.path,
        parentModuleId: createModuleDraft.parentModuleId,
      });
      if (!res?.ok) {
        setCreateModuleError(res?.error || 'CREATE_FAILED');
        return;
      }
      setModal(null);
    } finally {
      setCreateModuleSubmitting(false);
    }
  };

  const filteredRoles = roles.filter((r) => {
    const q = roleSearch.trim().toLowerCase();
    if (!q) return true;
    const byName = roleLabel(r).toLowerCase().includes(q);
    const byId = String(r.id).toLowerCase().includes(q);
    return byName || byId;
  });

  const modMatches = (m) =>
    !modSearch.trim() ||
    moduleLabel(m).toLowerCase().includes(modSearch.trim().toLowerCase()) ||
    (m.path && m.path.toLowerCase().includes(modSearch.trim().toLowerCase()));

  const renderTree = (parentId, depth = 0) => {
    if (selectedRoleId == null) return null;
    const rid = String(selectedRoleId);
    const kids = modChildren(modules, parentId).filter(modMatches);
    if (parentId != null && kids.length === 0) return null;

    return modChildren(modules, parentId)
      .filter(modMatches)
      .map((m) => {
        const id = String(m.id);
        const hasKids = modChildren(modules, id).length > 0;
        const open = expanded[id] !== false;
        const tri = moduleTriState(rid, id, perms, modules, standardActions, customByModule);
        const { total, active } = moduleSubtreeProgress(
          rid,
          id,
          perms,
          modules,
          standardActions,
          customByModule
        );
        const pct = total ? Math.round((active / total) * 100) : 0;

        return (
          <div key={id}>
            <div
              className={`${styles.treeRow} ${selectedModuleId === id ? styles.treeRowSel : ''}`}
              style={{ paddingLeft: 6 + depth * 14 }}
              onClick={() => setSelectedModuleId(id)}
              role="treeitem"
              aria-selected={selectedModuleId === id}
              aria-expanded={hasKids ? open : undefined}
            >
              {hasKids ? (
                <button
                  type="button"
                  className={styles.treeToggle}
                  disabled={!canUpdate || roleAssignmentsLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((ex) => ({ ...ex, [id]: !open }));
                  }}
                  aria-label={open ? 'Collapse' : 'Expand'}
                >
                  {open ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                </button>
              ) : (
                <span className={styles.treeToggle} />
              )}
              <TriCheckbox
                state={tri}
                ariaLabel={translate('dashboard.configuration.a11y.toggleModule')}
                onClick={() => toggleModuleAll(id)}
                disabled={!canUpdate || roleAssignmentsLoading}
              />
              <span className={styles.treeLabel}>{moduleLabel(m)}</span>
              <span className={styles.treeStat}>
                <span>
                  {active}/{total}
                </span>
                <span className={styles.bar}>
                  <span className={styles.barFill} style={{ width: `${pct}%` }} />
                </span>
              </span>
            </div>
            {hasKids && open && <div role="group">{renderTree(id, depth + 1)}</div>}
          </div>
        );
      });
  };

  const selectedModule = modules.find((m) => String(m.id) === String(selectedModuleId));
  const selectedCatalogActionCount = selectedModule
    ? getActionsForModule(String(selectedModule.id), standardActions, customByModule).length
    : 0;
  const canPersistModuleActions = !!(
    selectedModule &&
    (source !== 'api' || /^\d+$/.test(String(selectedModule.id)))
  );

  const headerActions = (
    <>
      {dirty && (
        <span className={styles.unsaved}>
          <FaExclamationTriangle className={styles.unsavedIcon} size={13} aria-hidden />
          {translate('dashboard.configuration.unsaved')}
        </span>
      )}
      <Button
        variant="outline"
        size="sm"
        disabled={!canUpdate || !dirty || roleAssignmentsLoading}
        onClick={handleDiscard}
        leftIcon={<FaUndo size={12} aria-hidden />}
      >
        {translate('dashboard.configuration.discard')}
      </Button>
      <Button
        variant="primary"
        size="sm"
        disabled={!canUpdate || !dirty || saving || roleAssignmentsLoading}
        loading={saving}
        onClick={() => handleSave(true)}
        leftIcon={<FaSave size={12} aria-hidden />}
      >
        {translate('dashboard.configuration.save')}
      </Button>
    </>
  );

  if (loading) {
    return (
      <>
        <PageHeader
          title={translate('dashboard.configuration.pageTitle')}
          subtitle={translate('dashboard.configuration.pageSubtitle')}
          icon={<FaCog />}
          breadcrumbs={['Dashboard', translate('dashboard.configuration.breadcrumb')]}
        />
        <div className={styles.empty}>
          {translate('dashboard.configuration.loading')}
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={translate('dashboard.configuration.pageTitle')}
        subtitle={translate('dashboard.configuration.pageSubtitle')}
        icon={<FaCog />}
        breadcrumbs={['Dashboard', translate('dashboard.configuration.breadcrumb')]}
        actions={headerActions}
      />
      <div className={styles.wrap}>
      {showLogoutAfterNoAccess ? (
        <div className={styles.deniedState}>
          <div className={styles.deniedCard} role="status" aria-live="polite">
            <div className={styles.deniedIconCircle} aria-hidden>
              <FaLock className={styles.deniedIcon} />
            </div>
            <h2 className={styles.deniedTitle}>
              {translate('global.accessDeniedTitle') || 'Acceso denegado'}
            </h2>
            <p className={styles.deniedMessage}>
              {translate('global.accessDeniedBody') ||
                'No tienes permisos para acceder a esta sección. Cierra sesión e inicia con un usuario autorizado.'}
            </p>
            <div className={styles.deniedCta} style={{ gap: 10 }}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => auth?.signOut?.()}
                leftIcon={<FaTimes size={14} aria-hidden />}
              >
                {translate('global.logout') || 'Logout'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => history.push('/dashboard/')}
                leftIcon={<FaHome size={14} aria-hidden />}
              >
                {translate('global.goHome') || 'Ir al inicio'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      {error && (
        <Badge variant="warning" showIcon>
          {translate('dashboard.configuration.loadWarning')}
        </Badge>
      )}

      {!showLogoutAfterNoAccess ? (
      <>
          <div className={styles.grid3}>
            <div className={styles.col}>
              <div className={styles.colHead}>
                <div className={styles.colTitle}>
                  {translate('dashboard.configuration.roles')}{' '}
                  <Badge variant="neutral" size="sm" showIcon={false}>
                    {roles.length}
                  </Badge>
                </div>
                <div className={styles.searchWrap}>
                  <FaSearch className={styles.searchIcon} size={12} />
                  <input
                    className={styles.searchInput}
                    placeholder={translate('dashboard.configuration.searchRole')}
                    value={roleSearch}
                    onChange={(e) => setRoleSearch(e.currentTarget.value)}
                  />
                </div>
              </div>
              <div className={styles.colBody}>
                {filteredRoles.length === 0 ? (
                  <div className={styles.empty}>{translate('dashboard.configuration.noRoles')}</div>
                ) : (
                  filteredRoles.map((r) => {
                    const d = countDiffForRole(perms, saved, roles, modules, standardActions, customByModule, r.id);
                    return (
                      <div
                        key={r.id}
                        className={`${styles.roleRow} ${String(selectedRoleId) === String(r.id) ? styles.roleRowActive : ''}`}
                        onClick={() => trySelectRole(r.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && trySelectRole(r.id)}
                      >
                        <div
                          className={styles.roleAvatar}
                          style={{ background: `${r.color}22`, color: r.color }}
                        >
                          {r.abbr}
                        </div>
                        <div className={styles.roleMeta}>
                          <div>
                            {roleLabel(r)}
                            <span className={styles.roleIdBadge} title={`${translate('dashboard.configuration.roleIdLabel')}: ${r.id}`}>
                              {translate('dashboard.configuration.roleIdLabel')}: {r.id}
                            </span>
                            {r.system && (
                              <Badge variant="error" size="sm" showIcon={false} style={{ marginLeft: 6 }}>
                                {translate('dashboard.configuration.systemRole')}
                              </Badge>
                            )}
                          </div>
                          <div className={styles.roleSub}>
                            {r.userCount != null && r.userCount > 0
                              ? `${r.userCount} ${translate('dashboard.configuration.users')}`
                              : ''}
                            {d > 0 && (
                              <span style={{ color: 'var(--warning-600, #d97706)' }}> · ● {d}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className={styles.col}>
              <div className={styles.colHead}>
                <div className={`${styles.colTitle} ${styles.colTitleToolbar}`}>
                  <span>
                    {translate('dashboard.configuration.modules')}
                    {selectedRoleId != null && (
                      <Badge variant="info" size="sm" showIcon={false} style={{ marginLeft: 8 }}>
                        {roleLabel(roles.find((x) => String(x.id) === String(selectedRoleId)) || {})}
                      </Badge>
                    )}
                  </span>
                  <div className={styles.treeToolbar}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={expandAll}
                      disabled={!canUpdate || roleAssignmentsLoading}
                      leftIcon={<FaAngleDoubleDown size={12} aria-hidden />}
                    >
                      {translate('dashboard.configuration.expandAll')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={collapseAll}
                      disabled={!canUpdate || roleAssignmentsLoading}
                      leftIcon={<FaAngleDoubleUp size={12} aria-hidden />}
                    >
                      {translate('dashboard.configuration.collapseAll')}
                    </Button>
                  </div>
                </div>
                <div className={styles.modulesHeadTools}>
                  <div className={styles.searchWrap}>
                    <FaSearch className={styles.searchIcon} size={12} />
                    <input
                      className={styles.searchInput}
                      placeholder={translate('dashboard.configuration.filterModules')}
                      value={modSearch}
                      onChange={(e) => setModSearch(e.currentTarget.value)}
                    />
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={openCreateModule}
                    disabled={!canCreate || roleAssignmentsLoading || creatingModule || creatingAction}
                    leftIcon={<FaPlus size={12} aria-hidden />}
                    title={translate('dashboard.configuration.createModule') || 'Crear módulo'}
                  >
                    {translate('dashboard.configuration.create') || 'Crear'}
                  </Button>
                </div>
              </div>
              <div className={styles.colBodyShell}>
                {renderRoleSwitchOverlay()}
                <div className={styles.colBodyScroll} role="tree">
                  {selectedRoleId == null ? (
                    <div className={styles.empty}>{translate('dashboard.configuration.pickRole')}</div>
                  ) : (
                    renderTree(null, 0)
                  )}
                </div>
              </div>
            </div>

            <div className={styles.col}>
              {!selectedModule ? (
                <div className={styles.colBodyShell}>
                  {renderRoleSwitchOverlay()}
                  <div className={styles.colBodyScroll}>
                    <div className={styles.empty}>
                      <h3 className={styles.actionsTitle} style={{ fontSize: '0.875rem' }}>
                        {translate('dashboard.configuration.noModuleTitle')}
                      </h3>
                      <p style={{ fontSize: '0.8125rem', marginTop: 8 }}>
                        {translate('dashboard.configuration.noModuleHint')}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className={styles.actionsHead}>
                    <div className={styles.colTitle}>{translate('dashboard.configuration.actions')}</div>
                    <div className={styles.actionsHeadRow}>
                      <div className={styles.actionsHeadMeta}>
                        <div className={styles.actionsTitle}>{moduleLabel(selectedModule)}</div>
                        <div className={styles.actionsPath} title={selectedModule.path}>
                          {selectedModule.path}
                        </div>
                      </div>
                      <div className={styles.actionsHeadButtons}>
                        {granularCatalog && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={openCreateAction}
                            disabled={
                              !canCreate ||
                              roleAssignmentsLoading ||
                              creatingModule ||
                              creatingAction ||
                              !canPersistModuleActions
                            }
                            leftIcon={<FaPlus size={12} aria-hidden />}
                            title={
                              !canPersistModuleActions
                                ? translate('dashboard.configuration.createActionNeedSavedModule')
                                : undefined
                            }
                          >
                            {translate('dashboard.configuration.createAction')}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={openEditModule}
                          disabled={!canUpdate || roleAssignmentsLoading}
                          leftIcon={<FaPen size={12} aria-hidden />}
                          title={translate('dashboard.configuration.editModule')}
                        >
                          {translate('dashboard.configuration.editModule')}
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className={styles.colBodyShell}>
                    {renderRoleSwitchOverlay()}
                    <div className={styles.colBodyScroll}>
                    {granularCatalog ? (
                      <>
                        <div className={styles.groupTitle} style={{ marginBottom: 8 }}>
                          {translate('dashboard.configuration.bliActionCatalog')}
                        </div>
                        {resolveCustom(String(selectedModule.id)).length === 0 ? (
                          <div className={styles.empty} style={{ fontSize: '0.8125rem' }}>
                            {translate('dashboard.configuration.noCatalogActionsForModule')}
                          </div>
                        ) : (
                          [...resolveCustom(String(selectedModule.id))]
                            .sort((a, b) => {
                              const la = String(a.label || a.code || '');
                              const lb = String(b.label || b.code || '');
                              const c = la.localeCompare(lb);
                              if (c !== 0) return c;
                              return String(a.code).localeCompare(String(b.code));
                            })
                            .map((a) => {
                              const rid = selectedRoleId != null ? String(selectedRoleId) : '';
                              const mid = String(selectedModule.id);
                              const slice = rid ? perms[rid]?.[mid] : null;
                              const on = !!(
                                selectedRoleId &&
                                isActionEnabledInSlice(slice, a.code, selectedCatalogActionCount)
                              );
                              const canEditSlug =
                                !!a.actionId && canPersistModuleActions;
                              const rowKey =
                                a.actionId != null ? `a-${a.actionId}` : `code-${a.code}`;
                              return (
                                <div key={rowKey} className={styles.actionRow}>
                                  <div className={styles.actionRowLeading}>
                                    {canEditSlug ? (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className={styles.actionEditIconBtn}
                                        onClick={() => openEditActionCode(a)}
                                        disabled={!canUpdate || roleAssignmentsLoading}
                                        aria-label={translate('dashboard.configuration.editActionCode')}
                                        title={translate('dashboard.configuration.editActionCode')}
                                      >
                                        <FaPen size={12} aria-hidden />
                                      </Button>
                                    ) : (
                                      <span className={styles.actionEditIconSpacer} aria-hidden />
                                    )}
                                    <span className={styles.actionCode}>{a.code}</span>
                                  </div>
                                  <div className={styles.actionText}>
                                    <div className={styles.actionLabel}>{a.label || a.code}</div>
                                    {a.desc ? <div className={styles.actionDesc}>{a.desc}</div> : null}
                                  </div>
                                  <Switch
                                    on={on}
                                    disabled={!canUpdate || selectedRoleId == null || roleAssignmentsLoading}
                                    ariaLabel={a.label || a.code}
                                    onToggle={() => toggleAction(selectedModule.id, a)}
                                  />
                                </div>
                              );
                            })
                        )}
                      </>
                    ) : (
                      <>
                        <div className={styles.groupTitle}>{translate('dashboard.configuration.standard')}</div>
                        {standardActions.map((a) => {
                          const rid = selectedRoleId != null ? String(selectedRoleId) : '';
                          const mid = String(selectedModule.id);
                          const slice = rid ? perms[rid]?.[mid] : null;
                          const on = !!(
                            selectedRoleId &&
                            isActionEnabledInSlice(slice, a.code, selectedCatalogActionCount)
                          );
                          return (
                            <div key={a.code} className={styles.actionRow}>
                              <span className={styles.actionCode}>{a.code}</span>
                              <div className={styles.actionText}>
                                <div className={styles.actionLabel}>{a.label}</div>
                                <div className={styles.actionDesc}>{a.desc}</div>
                              </div>
                              <Switch
                                on={on}
                                disabled={!canUpdate || selectedRoleId == null || roleAssignmentsLoading}
                                ariaLabel={a.label}
                                onToggle={() => toggleAction(selectedModule.id, a)}
                              />
                            </div>
                          );
                        })}
                        {(resolveCustom(String(selectedModule.id)).length > 0 ||
                          (customByModule[String(selectedModule.id)] || []).length > 0) && (
                          <>
                            <div className={styles.groupTitle}>{translate('dashboard.configuration.custom')}</div>
                            {resolveCustom(String(selectedModule.id)).map((a) => {
                              const rid = selectedRoleId != null ? String(selectedRoleId) : '';
                              const mid = String(selectedModule.id);
                              const slice = rid ? perms[rid]?.[mid] : null;
                              const on = !!(
                                selectedRoleId &&
                                isActionEnabledInSlice(slice, a.code, selectedCatalogActionCount)
                              );
                              return (
                                <div key={a.code} className={styles.actionRow}>
                                  <span className={styles.actionCode}>{a.code}</span>
                                  <div className={styles.actionText}>
                                    <div className={styles.actionLabel}>{a.label}</div>
                                    {a.desc ? <div className={styles.actionDesc}>{a.desc}</div> : null}
                                  </div>
                                  <Switch
                                    on={on}
                                    disabled={!canUpdate || selectedRoleId == null || roleAssignmentsLoading}
                                    ariaLabel={a.label}
                                    onToggle={() => toggleAction(selectedModule.id, a)}
                                  />
                                </div>
                              );
                            })}
                          </>
                        )}
                      </>
                    )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {dirty && (
            <div className={styles.diffBar}>
              <div className={styles.diffTags}>
                <span>{translate('dashboard.configuration.pendingChanges')}</span>
                <span className={styles.tagAdd}>+{diff.added}</span>
                <span className={styles.tagRem}>-{diff.removed}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setModal('diff')}
                  disabled={roleAssignmentsLoading}
                  leftIcon={<FaListUl size={12} aria-hidden />}
                >
                  {translate('dashboard.configuration.viewDiff')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleSave(true)}
                  loading={saving}
                  disabled={!canUpdate || roleAssignmentsLoading}
                  leftIcon={<FaSave size={12} aria-hidden />}
                >
                  {translate('dashboard.configuration.save')}
                </Button>
              </div>
            </div>
          )}
      </>
      ) : null}
      </div>

      {modal === 'unsavedRole' && createPortal(
        <div className={styles.modalOverlay} role="dialog" aria-modal="true">
          <div className={styles.modalBox}>
            <ModalStatusHeader variant="warning" title={translate('dashboard.configuration.unsavedTitle')} />
            <p className={styles.modalText}>
              {translate('dashboard.configuration.unsavedBody')}
            </p>
            <div className={styles.modalActions}>
              <Button variant="outline" onClick={() => { setModal(null); setPendingRoleId(null); }} leftIcon={<FaTimes size={14} aria-hidden />}>
                {translate('global.cancel') || 'Cancelar'}
              </Button>
              <Button variant="outline" onClick={discardAndSelectPending} leftIcon={<FaUndo size={14} aria-hidden />}>
                {translate('dashboard.configuration.discardAndSwitch')}
              </Button>
              <Button variant="primary" onClick={saveAndSelectPending} loading={saving} leftIcon={<FaSave size={14} aria-hidden />}>
                {translate('dashboard.configuration.saveAndSwitch')}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {modal === 'diff' && createPortal(
        <div className={styles.modalOverlay} role="dialog" aria-modal="true" onClick={() => setModal(null)}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalTitle}>{translate('dashboard.configuration.diffTitle')}</div>
            <div className={styles.diffList}>
              {diff.changes.length === 0 ? (
                <div className={styles.empty}>{translate('dashboard.configuration.noDiff')}</div>
              ) : (
                diff.changes.map((c, i) => (
                  <div key={i} className={styles.diffItem}>
                    <strong>{c.mod}</strong> · {c.code} (
                    {c.labelKey ? translate(c.labelKey) : c.label}) —{' '}
                    {c.cur ? translate('dashboard.configuration.added') : translate('dashboard.configuration.removed')}
                  </div>
                ))
              )}
            </div>
            <div className={styles.modalActions}>
              <Button variant="primary" onClick={() => setModal(null)} leftIcon={<FaTimes size={14} aria-hidden />}>
                {translate('dashboard.configuration.close')}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {modal === 'saved' && createPortal(
        <div className={styles.modalOverlay} role="dialog" aria-modal="true">
          <div className={styles.modalBox}>
            <ModalStatusHeader variant="success" title={translate('dashboard.configuration.savedTitle')} />
            <p style={{ fontSize: '0.875rem' }}>{translate('dashboard.configuration.savedBody')}</p>
            <div className={styles.modalActions}>
              <Button variant="primary" onClick={() => setModal(null)} leftIcon={<FaCheck size={14} aria-hidden />}>
                {translate('dashboard.configuration.close')}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {modal === 'saveError' && createPortal(
        <div className={styles.modalOverlay} role="dialog" aria-modal="true">
          <div className={styles.modalBox}>
            <ModalStatusHeader variant="error" title={translate('dashboard.configuration.saveErrorTitle')} />
            <p style={{ fontSize: '0.875rem' }}>{translate('dashboard.configuration.saveErrorBody')}</p>
            {saveErrorDetail ? (
              <p className={styles.modalText} style={{ marginTop: 8, fontFamily: 'ui-monospace, monospace', fontSize: '0.75rem' }}>
                {saveErrorDetail}
              </p>
            ) : null}
            <div className={styles.modalActions}>
              <Button variant="primary" onClick={() => { setSaveErrorDetail(null); setModal(null); }} leftIcon={<FaTimes size={14} aria-hidden />}>
                {translate('dashboard.configuration.close')}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {modal === 'noCatalogAccess' && createPortal(
        <div className={styles.modalOverlay} role="dialog" aria-modal="true">
          <div className={styles.modalBox}>
            <ModalStatusHeader
              variant="error"
              title={translate('global.accessDeniedTitle') || 'Acceso denegado'}
            />
            <p className={styles.modalText}>
              {translate('global.accessDeniedBody') ||
                'No tienes permisos para acceder a esta sección o no fue posible cargar el catálogo de permisos.'}
            </p>
            {permissionDenied ? (
              <p className={styles.modalText} style={{ marginTop: 8 }}>
                {translate('global.accessDeniedHint') || 'Pide acceso a un administrador o inicia sesión con otra cuenta.'}
              </p>
            ) : null}
            <div className={styles.modalActions}>
              <Button
                variant="primary"
                onClick={() => {
                  setNoAccessAcknowledged(true);
                  setModal(null);
                  setShowLogoutAfterNoAccess(true);
                }}
                leftIcon={<FaCheck size={14} aria-hidden />}
              >
                {translate('global.ok') || 'OK'}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {modal === 'createModule' && createPortal(
        <div className={styles.modalOverlay} role="dialog" aria-modal="true" onClick={() => setModal(null)}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalTitle}>{translate('dashboard.configuration.createModule') || 'Crear módulo'}</div>
            <p className={styles.modalText} style={{ marginBottom: 12 }}>
              {source === 'api'
                ? (translate('dashboard.configuration.createModuleApiNote') ||
                  'Nota: por ahora se crea solo en la UI (no hay endpoint de backend).')
                : (translate('dashboard.configuration.createModuleHelp') ||
                  'Completa los datos y selecciona un módulo padre si es un submódulo.')}
            </p>

            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <div className={styles.colTitle}>{translate('dashboard.configuration.moduleName') || 'Nombre'}</div>
                <input
                  className={styles.searchInput}
                  value={createModuleDraft.moduleName}
                  onChange={(e) => {
                    const moduleName = e.currentTarget.value;
                    setCreateModuleDraft((d) => ({ ...d, moduleName }));
                  }}
                  placeholder={translate('dashboard.configuration.moduleNamePh') || 'Ej: Lockers'}
                />
              </div>

              <div>
                <div className={styles.colTitle}>{translate('dashboard.configuration.modulePath') || 'Path'}</div>
                <input
                  className={styles.searchInput}
                  value={createModuleDraft.path}
                  onChange={(e) => {
                    const path = e.currentTarget.value;
                    setCreateModuleDraft((d) => ({ ...d, path }));
                  }}
                  placeholder={translate('dashboard.configuration.modulePathPh') || 'Ej: /lockers'}
                />
              </div>

              <div>
                <div className={styles.colTitle}>{translate('dashboard.configuration.parentModule') || 'Módulo padre (opcional)'}</div>
                <select
                  className={styles.searchInput}
                  value={createModuleDraft.parentModuleId}
                  onChange={(e) => {
                    const parentModuleId = e.currentTarget.value;
                    setCreateModuleDraft((d) => ({ ...d, parentModuleId }));
                  }}
                >
                  <option value="">{translate('dashboard.configuration.noParent') || 'Sin padre (módulo raíz)'}</option>
                  {modules
                    .slice()
                    .sort((a, b) => String(moduleLabel(a)).localeCompare(String(moduleLabel(b))))
                    .map((m) => (
                      <option key={String(m.id)} value={String(m.id)}>
                        {moduleLabel(m)}
                      </option>
                    ))}
                </select>
              </div>

              {createModuleError && (
                <Badge variant="warning" showIcon>
                  {createModuleError === 'REQUIRED_FIELDS'
                    ? (translate('dashboard.configuration.requiredFields') || 'Nombre y Path son obligatorios.')
                    : createModuleError === 'CREATE_FAILED'
                      ? (translate('dashboard.configuration.createFailed') || 'No se pudo crear el módulo.')
                      : createModuleError}
                </Badge>
              )}
            </div>

            <div className={styles.modalActions}>
              <Button variant="outline" onClick={() => setModal(null)} leftIcon={<FaTimes size={14} aria-hidden />}>
                {translate('global.cancel') || 'Cancelar'}
              </Button>
              <Button
                variant="primary"
                onClick={submitCreateModule}
                loading={createModuleSubmitting}
                disabled={!canCreate || createModuleSubmitting}
                leftIcon={<FaCheck size={14} aria-hidden />}
              >
                {translate('dashboard.configuration.create') || 'Crear'}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {modal === 'createAction' && createPortal(
        <div className={styles.modalOverlay} role="dialog" aria-modal="true" onClick={() => setModal(null)}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalTitle}>{translate('dashboard.configuration.createActionTitle')}</div>

            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <div className={styles.colTitle}>{translate('dashboard.configuration.actionCode')}</div>
                <input
                  ref={createActionCodeInputRef}
                  className={styles.searchInput}
                  value={createActionDraft.actionCode}
                  onChange={(e) => {
                    setCreateActionError(null);
                    const actionCode = e.currentTarget.value;
                    setCreateActionDraft((d) => ({ ...d, actionCode }));
                  }}
                  placeholder={translate('dashboard.configuration.actionCodePh')}
                  autoComplete="off"
                />
              </div>

              {createActionError && (
                <Badge variant="warning" showIcon>
                  {createActionError === 'ACTION_CODE_REQUIRED'
                    ? translate('dashboard.configuration.actionCodeRequired')
                    : createActionError === 'DUPLICATE_ACTION_CODE'
                      ? translate('dashboard.configuration.duplicateActionCode')
                      : String(createActionError).includes('ACTION_ALREADY_LINKED_TO_MODULE')
                        ? translate('dashboard.configuration.actionAlreadyLinkedToModule')
                        : createActionError === 'BLI_MODULE_ACTION_CREATE_VALIDATION' ||
                            createActionError === 'CREATE_FAILED'
                          ? translate('dashboard.configuration.createActionFailed')
                          : createActionError}
                </Badge>
              )}
            </div>

            <div className={styles.modalActions}>
              <Button variant="outline" onClick={() => setModal(null)} leftIcon={<FaTimes size={14} aria-hidden />}>
                {translate('global.cancel') || 'Cancelar'}
              </Button>
              <Button
                variant="primary"
                onClick={submitCreateAction}
                loading={createActionSubmitting}
                disabled={!canCreate || createActionSubmitting}
                leftIcon={<FaCheck size={14} aria-hidden />}
              >
                {translate('dashboard.configuration.create')}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {modal === 'editAction' && editActionDraft != null && createPortal(
        <div
          className={styles.modalOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-action-code-dialog-title"
          onClick={() => {
            setModal(null);
            setEditActionDraft(null);
            setEditActionError(null);
          }}
        >
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div id="edit-action-code-dialog-title" className={styles.modalTitle}>
              {translate('dashboard.configuration.editActionCodeTitle')}
            </div>
            <p className={styles.modalText} style={{ marginBottom: 12 }}>
              <span className={styles.actionCode} style={{ display: 'inline-block', marginRight: 6 }}>
                {editActionDraft.originalCode}
              </span>
              <span style={{ color: 'var(--text-secondary, #6b7280)', fontSize: '0.8125rem' }}>
                {editActionDraft.displayLabel}
              </span>
            </p>
            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <div className={styles.colTitle}>{translate('dashboard.configuration.actionCode')}</div>
                <input
                  className={styles.searchInput}
                  value={editActionDraft.code}
                  onChange={(e) => {
                    setEditActionError(null);
                    const code = e.currentTarget.value;
                    setEditActionDraft((d) => (d ? { ...d, code } : d));
                  }}
                  placeholder={translate('dashboard.configuration.actionCodePh')}
                  autoComplete="off"
                  spellCheck={false}
                  disabled={editActionSubmitting}
                />
              </div>
              {editActionError ? (
                <Badge variant="warning" showIcon>
                  {editActionError}
                </Badge>
              ) : null}
            </div>
            <div className={styles.modalActions}>
              <Button
                variant="outline"
                onClick={() => {
                  setModal(null);
                  setEditActionDraft(null);
                  setEditActionError(null);
                }}
                disabled={editActionSubmitting}
                leftIcon={<FaTimes size={14} aria-hidden />}
              >
                {translate('global.cancel') || 'Cancelar'}
              </Button>
              <Button
                variant="primary"
                onClick={submitEditActionCode}
                loading={editActionSubmitting}
                disabled={!canUpdate || editActionSubmitting}
                leftIcon={<FaCheck size={14} aria-hidden />}
              >
                {translate('global.save')}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {modal === 'editModule' && createPortal(
        <div className={styles.modalOverlay} role="dialog" aria-modal="true" onClick={() => setModal(null)}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalTitle}>{translate('dashboard.configuration.editModule')}</div>
            <p className={styles.modalText} style={{ marginBottom: 12 }}>
              {source === 'api'
                ? translate('dashboard.configuration.editModuleApiNote')
                : translate('dashboard.configuration.editModuleHelp')}
            </p>

            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <div className={styles.colTitle}>{translate('dashboard.configuration.moduleName')}</div>
                <input
                  className={styles.searchInput}
                  value={editModuleDraft.moduleName}
                  onChange={(e) => {
                    const moduleName = e.currentTarget.value;
                    setEditModuleDraft((d) => ({ ...d, moduleName }));
                  }}
                  placeholder={translate('dashboard.configuration.moduleNamePh')}
                />
              </div>

              <div>
                <div className={styles.colTitle}>{translate('dashboard.configuration.modulePath')}</div>
                <input
                  className={styles.searchInput}
                  value={editModuleDraft.path}
                  onChange={(e) => {
                    const path = e.currentTarget.value;
                    setEditModuleDraft((d) => ({ ...d, path }));
                  }}
                  placeholder={translate('dashboard.configuration.modulePathPh')}
                />
              </div>

              <div>
                <div className={styles.colTitle}>{translate('dashboard.configuration.parentModule')}</div>
                <select
                  className={styles.searchInput}
                  value={editModuleDraft.parentModuleId}
                  onChange={(e) => {
                    const parentModuleId = e.currentTarget.value;
                    setEditModuleDraft((d) => ({ ...d, parentModuleId }));
                  }}
                >
                  <option value="">{translate('dashboard.configuration.noParent')}</option>
                  {modules
                    .filter((m) => !parentExcludeIdsForEdit.has(String(m.id)))
                    .slice()
                    .sort((a, b) => String(moduleLabel(a)).localeCompare(String(moduleLabel(b))))
                    .map((m) => (
                      <option key={String(m.id)} value={String(m.id)}>
                        {moduleLabel(m)}
                      </option>
                    ))}
                </select>
              </div>

              {editModuleError && (
                <Badge variant="warning" showIcon>
                  {editModuleError === 'REQUIRED_FIELDS'
                    ? translate('dashboard.configuration.requiredFields')
                    : editModuleError === 'UPDATE_FAILED'
                      ? translate('dashboard.configuration.updateFailed')
                      : editModuleError}
                </Badge>
              )}
            </div>

            <div className={styles.modalActions}>
              <Button variant="outline" onClick={() => setModal(null)} leftIcon={<FaTimes size={14} aria-hidden />}>
                {translate('global.cancel') || 'Cancelar'}
              </Button>
              <Button
                variant="primary"
                onClick={submitEditModule}
                loading={editModuleSubmitting}
                disabled={!canUpdate || editModuleSubmitting}
                leftIcon={<FaCheck size={14} aria-hidden />}
              >
                {translate('global.save')}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
